// @flow

import { combineEpics } from 'redux-observable';
import io from 'socket.io-client';
import { __ } from '../../i18n/l10n';
import type { Socket } from 'socket.io-client';
import { filter, switchMap, map, distinct } from 'rxjs/operators';
import { of, concat, pipe, fromEvent, merge, EMPTY, type Observable } from 'rxjs';
import config from '../../config/app.config.js';
import { generateId } from '../../utils/CommonUtils';
import {
  WS_CONNECT,
  WS_CONNECT_SUCCESS,
  WS_CONNECT_ERROR,
  WS_DISCONNECT,
  WS_CONNECT_DEPOSIT,
  WS_CONNECT_DEPOSIT_SUCCESS,
  WS_DISCONNECT_DEPOSIT,
  WS_DISPLAY_ERROR_LAYER,
  WS_CLEAR_RESPONSES,
  WS_CLEAR_USER_REGISTRATION_RESPONSES,
  WS_CLEAR_DEPOSIT_RESPONSES,
  WS_DISCONNECTED_DEPOSIT,
  WS_DISCONNECTED_REGISTRATION,
  SAVE_JWT,
} from '../actions';
import type {
  RootState,
  RootActions,
  WebsocketActions,
  StateObservable,
  MessageResponse,
  MessageRequest,
  ApiRequest,
  DepositActions,
  UserRegistrationActions,
} from '../types';
import type { ActionsObservable } from 'redux-observable';

const ENDPOINT = config.apiURL;

let ws: ?Socket = null;
let wsDeposit: ?Socket = null;

const getWebsocket = () => ws;

const sendMessageWebsocket = <T>(request: ApiRequest<T>, socket: ?Socket, ns: string) => {
  if (socket) {
    const json: MessageRequest<T> = {
      ts: Date.now(),
      ns,
      method: request.method,
      params: request.params,
      id: request.id ? request.id : generateId(request.method),
    };

    socket.emit('message', JSON.stringify(json));
  }
};

export const initConnect = (
  state$: StateObservable<RootState>,
  codeToExecute: (action: any) => ActionsObservable<any>
): rxjs$UnaryFunction<Observable<any>, Observable<any>> => {
  return pipe(
    switchMap((action) => {
      if (state$.value.websocket.connected) {
        return codeToExecute(action);
      }

      const toPipe = [];
      if (!state$.value.websocket.connecting) {
        toPipe.push(of({ type: WS_CONNECT }));
      }

      toPipe.push(
        state$.pipe(
          filter((state) => state.websocket.connected),
          distinct((state) => state.websocket.connected),
          switchMap(() => {
            return codeToExecute(action);
          })
        )
      );

      return concat(...toPipe);
    })
  );
};

export const afterConnectDeposit = (
  state$: StateObservable<RootState>,
  codeToExecute: any
): rxjs$UnaryFunction<Observable<any>, Observable<any>> => {
  return pipe(
    switchMap((action: WebsocketActions) => {
      if (state$.value.websocket.connectedDeposit) {
        return codeToExecute(action);
      }

      const toPipe = [];
      if (!state$.value.websocket.connectingDeposit) {
        toPipe.push(of({ type: WS_CONNECT_DEPOSIT }));
      }

      // Not connected, retry
      if (state$.value.websocket.connectingDeposit && !state$.value.websocket.connectedDeposit) {
        toPipe.push(of({ type: WS_CONNECT_DEPOSIT }));
      }

      toPipe.push(
        state$.pipe(
          filter((state) => state.websocket.connectedDeposit),
          distinct((state) => state.websocket.connectedDeposit),
          switchMap(() => {
            return codeToExecute(action);
          })
        )
      );

      return concat(...toPipe);
    })
  );
};

export const sendMessage = <P, R, E>(
  payload: ApiRequest<P>,
  websocket: ?Socket = ws,
  ns: string = config.apiNS
): Observable<MessageResponse<R, E>> => {
  const json: ApiRequest<P> = {
    method: payload.method,
    params: payload.params,
    id: payload.id || generateId(payload.method),
  };

  sendMessageWebsocket(json, websocket, ns);

  const message$ = fromEvent(websocket, 'message');

  return message$.pipe(filter((messagePayload) => messagePayload.id === json.id));
};

export const sendMessageDeposit = <P, R, E>(
  payload: ApiRequest<P>
): Observable<MessageResponse<R, E>> => {
  return sendMessage(payload, wsDeposit, config.apiNSDeposit);
};

export const connectEpic = (action$: ActionsObservable<WebsocketActions>) =>
  action$.pipe(
    filter((action) => action.type === WS_CONNECT),
    switchMap((action: WebsocketActions) => {
      if (config.isMockUp) return EMPTY;
      ws = io(ENDPOINT, {
        path: '/user/registration/socket/socket.io',
        timeout: config.socket.timeout,
        reconnectionAttempts: config.socket.reconnectionAttempts,
      });

      const connect = fromEvent(ws, 'connect').pipe(
        map(() => {
          return { type: WS_CONNECT_SUCCESS };
        })
      );

      const failed = fromEvent(ws, 'connect_error').pipe(
        map(() => {
          if (!config.isMockUp) {
            return {
              type: WS_DISPLAY_ERROR_LAYER,
              payload: {
                retryAction: action,
                codeMessage: 'WEBSOCKET',
                message: __('It seems there is a connection problem'),
              },
            };
          }
          return { type: WS_CONNECT_ERROR };
        })
      );

      return merge(connect, failed);
    })
  );

// Todo Fix type
const getTokenFromValue = (value): any => {
  const tokenValue = value && value.result && value.result.data && value.result.data.jwt;
  return tokenValue ? tokenValue : null;
};

export const watchJwtEpic = (action$: ActionsObservable<UserRegistrationActions>) =>
  action$.pipe(
    filter((action) => action.type === WS_CONNECT_SUCCESS),
    switchMap(() => {
      return fromEvent(ws, 'message').pipe(
        filter((value) => getTokenFromValue(value)),
        map((value) => {
          return {
            type: SAVE_JWT,
            payload: getTokenFromValue(value),
          };
        })
      );
    })
  );

export const connectSuccessEpic = (action$: ActionsObservable<WebsocketActions>) =>
  action$.pipe(
    filter((action) => action.type === WS_CONNECT_SUCCESS),
    switchMap(() => {
      return fromEvent(ws, 'disconnect').pipe(
        map(() => {
          return { type: WS_DISCONNECT };
        })
      );
    })
  );

export const disconnectedEpic = (action$: ActionsObservable<UserRegistrationActions>) =>
  action$.pipe(
    filter((action) => action.type === WS_DISCONNECT),
    switchMap(() => {
      return of({ type: WS_DISCONNECTED_REGISTRATION });
    })
  );

export const connectDepositEpic = (action$: ActionsObservable<WebsocketActions>) =>
  action$.pipe(
    filter((action) => action.type === WS_CONNECT_DEPOSIT),
    switchMap((action: WebsocketActions) => {
      if (config.isMockUp) return EMPTY;
      wsDeposit = io(ENDPOINT, {
        path: '/pcidss/front/deposit/socket/socket.io',
        timeout: config.socket.timeout,
        reconnectionAttempts: config.socket.reconnectionAttempts,
      });

      const connect = fromEvent(wsDeposit, 'connect').pipe(
        map(() => {
          return { type: WS_CONNECT_DEPOSIT_SUCCESS };
        })
      );

      // TODO: quand on implémentera le deposit, il faudra vérifier quel event on récupère
      // Probablement à changer par connect_error et/ou connect_timeout
      const failed = fromEvent(wsDeposit, 'reconnect_failed').pipe(
        map(() => {
          if (!config.isMockUp) {
            return {
              type: WS_DISPLAY_ERROR_LAYER,
              payload: {
                retryAction: action,
                codeMessage: 'WEBSOCKET',
                message: __('It seems there is a connection problem'),
              },
            };
          }
          return { type: WS_CONNECT_DEPOSIT_SUCCESS };
        })
      );

      return merge(connect, failed);
    })
  );

export const connectDepositSuccessEpic = (action$: ActionsObservable<WebsocketActions>) =>
  action$.pipe(
    filter((action) => action.type === WS_CONNECT_DEPOSIT_SUCCESS),
    switchMap(() => {
      return fromEvent(wsDeposit, 'disconnect').pipe(
        map(() => {
          return { type: WS_DISCONNECT_DEPOSIT };
        })
      );
    })
  );

export const disconnectedDepositEpic = (action$: ActionsObservable<DepositActions>) =>
  action$.pipe(
    filter((action) => action.type === WS_DISCONNECT_DEPOSIT),
    switchMap(() => {
      return of({ type: WS_DISCONNECTED_DEPOSIT });
    })
  );

export const clearResponsesEpic = (action$: ActionsObservable<RootActions>) =>
  action$.pipe(
    filter((action) => action.type === WS_CLEAR_RESPONSES),
    switchMap(() => {
      return of(
        { type: WS_CLEAR_USER_REGISTRATION_RESPONSES },
        { type: WS_CLEAR_DEPOSIT_RESPONSES }
      );
    })
  );

export const websocketEpics = combineEpics(
  connectEpic,
  watchJwtEpic,
  connectSuccessEpic,
  disconnectedEpic,
  connectDepositEpic,
  connectDepositSuccessEpic,
  disconnectedDepositEpic,
  clearResponsesEpic
);
